import { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, message, Row, Col, Card, Space, Tag, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ReloadOutlined, KeyOutlined, CopyOutlined } from '@ant-design/icons';
import { screenAPI } from '../services/api';
import { themeColors } from '../config/theme';
import { useAuthStore } from '../store/authStore';

const { Text, Paragraph } = Typography;

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:4000';
const SOCKET_API_URL = `${SOCKET_URL}/api`;

const RESOLUTION_PRESETS = [
  { key: '1920x1080', label: '1920 x 1080 (Full HD, Landscape)', width: 1920, height: 1080 },
  { key: '1280x720', label: '1280 x 720 (HD, Landscape)', width: 1280, height: 720 },
  { key: '3840x2160', label: '3840 x 2160 (4K, Landscape)', width: 3840, height: 2160 },
  { key: '1080x1920', label: '1080 x 1920 (Full HD, Portrait)', width: 1080, height: 1920 },
];

const presetKeyForSize = (width, height) => {
  const match = RESOLUTION_PRESETS.find((p) => p.width === width && p.height === height);
  return match ? match.key : width || height ? 'custom' : undefined;
};

const VIDEO_FIT_MODES = [
  { value: 'fill', label: 'Fill Screen (Cover) — fills completely, may crop edges' },
  { value: 'contain', label: 'Fit to Object (Contain) — shows entire video, may add black bars' },
];

export default function Screens() {
  const isSubAdmin = useAuthStore((state) => state.user?.role === 'sub_admin');
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);

  useEffect(() => {
    fetchScreens();
  }, [pagination]);

  const fetchScreens = async () => {
    try {
      setLoading(true);
      const response = await screenAPI.list({
        page: pagination.page,
        limit: pagination.limit,
      });
      setScreens(response.data.data.data);
      setTotal(response.data.data.pagination.total);
    } catch (error) {
      message.error('Failed to load screens');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (values) => {
    try {
      // Derive the final width/height from the selected preset at save time
      // (rather than trusting the hidden resolutionWidth/resolutionHeight
      // fields alone) since those Form.Items only mount when "Custom" is
      // selected and otherwise never get registered with the form.
      let resolutionWidth = values.resolutionWidth;
      let resolutionHeight = values.resolutionHeight;
      if (values.resolutionPreset && values.resolutionPreset !== 'custom') {
        const preset = RESOLUTION_PRESETS.find((p) => p.key === values.resolutionPreset);
        if (preset) {
          resolutionWidth = preset.width;
          resolutionHeight = preset.height;
        }
      }

      if (editingId) {
        await screenAPI.update(editingId, {
          screenCode: values.screen_code,
          screenName: values.screen_name,
          location: values.location,
          resolutionWidth,
          resolutionHeight,
          videoFitMode: values.videoFitMode,
          videoWidth: values.videoWidth,
          videoHeight: values.videoHeight,
          videoOffsetX: values.videoOffsetX,
          videoOffsetY: values.videoOffsetY,
        });
        message.success('Screen updated successfully');
      } else {
        await screenAPI.create({
          screenCode: values.screen_code,
          screenName: values.screen_name,
          location: values.location,
          resolutionWidth,
          resolutionHeight,
          videoFitMode: values.videoFitMode,
          videoWidth: values.videoWidth,
          videoHeight: values.videoHeight,
          videoOffsetX: values.videoOffsetX,
          videoOffsetY: values.videoOffsetY,
        });
        message.success('Screen created successfully');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingId(null);
      fetchScreens();
    } catch (error) {
      message.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      ...record,
      resolutionWidth: record.resolution_width,
      resolutionHeight: record.resolution_height,
      resolutionPreset: presetKeyForSize(record.resolution_width, record.resolution_height),
      videoFitMode: record.video_fit_mode || 'fill',
      videoWidth: record.video_width,
      videoHeight: record.video_height,
      videoOffsetX: record.video_offset_x,
      videoOffsetY: record.video_offset_y,
    });
    setIsModalOpen(true);
  };

  const handleGenerateToken = async (record) => {
    try {
      setTokenLoading(true);
      setTokenModalOpen(true);
      const response = await screenAPI.generateToken(record.id);
      setTokenData(response.data.data);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to generate token');
      setTokenModalOpen(false);
    } finally {
      setTokenLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Screen',
      content: 'Are you sure you want to delete this screen?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await screenAPI.delete(id);
          message.success('Screen deleted successfully');
          fetchScreens();
        } catch (error) {
          message.error('Failed to delete screen');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Screen Code',
      dataIndex: 'screen_code',
      key: 'screen_code',
      width: 150,
    },
    {
      title: 'Name',
      dataIndex: 'screen_name',
      key: 'screen_name',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Resolution',
      key: 'resolution',
      render: (_, record) =>
        record.resolution_width && record.resolution_height
          ? `${record.resolution_width} x ${record.resolution_height}`
          : '—',
    },
    {
      title: 'Fit Mode',
      key: 'video_fit_mode',
      render: (_, record) =>
        record.video_fit_mode === 'contain' ? 'Fit to Object' : 'Fill Screen',
    },
    {
      title: 'Video Canvas',
      key: 'video_canvas',
      render: (_, record) =>
        record.video_width && record.video_height
          ? `${record.video_width} x ${record.video_height} @ (${record.video_offset_x || 0},${record.video_offset_y || 0})`
          : 'Full screen',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'online' ? 'green' : 'red'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Last Seen',
      dataIndex: 'last_seen',
      key: 'last_seen',
      render: (date) => (date ? new Date(date).toLocaleString() : 'Never'),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => handleGenerateToken(record)}
            title="Generate Screen Token"
          />
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          {!isSubAdmin && (
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        {!isSubAdmin && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingId(null);
                form.resetFields();
                setIsModalOpen(true);
              }}
            >
              Add Screen
            </Button>
          </Col>
        )}
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchScreens} loading={loading} />
        </Col>
      </Row>

      <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
        <Table
          columns={columns}
          dataSource={screens}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total,
            onChange: (page, pageSize) => {
              setPagination({ page, limit: pageSize });
            },
          }}
          rowKey="id"
          style={{ color: themeColors.text }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Screen' : 'Create Screen'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setEditingId(null);
        }}
        style={{ backgroundColor: themeColors.bgElevated }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateOrUpdate}
          style={{ marginTop: '20px' }}
        >
          <Form.Item
            name="screen_code"
            label="Screen Code"
            rules={[{ required: true, message: 'Screen code is required' }]}
          >
            <Input placeholder="e.g., SCREEN001" disabled={!!editingId} />
          </Form.Item>

          <Form.Item
            name="screen_name"
            label="Screen Name"
            rules={[{ required: true, message: 'Screen name is required' }]}
          >
            <Input placeholder="e.g., Main Lobby" />
          </Form.Item>

          <Form.Item name="location" label="Location">
            <Input placeholder="e.g., Building A, Floor 1" />
          </Form.Item>

          <Form.Item name="resolutionPreset" label="Resolution">
            <Select
              placeholder="Select a resolution"
              allowClear
              options={[...RESOLUTION_PRESETS.map((p) => ({ value: p.key, label: p.label })), { value: 'custom', label: 'Custom' }]}
              onChange={(key) => {
                const preset = RESOLUTION_PRESETS.find((p) => p.key === key);
                if (preset) {
                  form.setFieldsValue({ resolutionWidth: preset.width, resolutionHeight: preset.height });
                } else if (key === undefined) {
                  form.setFieldsValue({ resolutionWidth: undefined, resolutionHeight: undefined });
                }
              }}
            />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.resolutionPreset !== cur.resolutionPreset}>
            {({ getFieldValue }) =>
              getFieldValue('resolutionPreset') === 'custom' ? (
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="resolutionWidth" label="Width (px)">
                      <InputNumber min={1} style={{ width: '100%' }} placeholder="1920" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="resolutionHeight" label="Height (px)">
                      <InputNumber min={1} style={{ width: '100%' }} placeholder="1080" />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="videoFitMode"
            label="Video Fit Mode"
            initialValue="fill"
            tooltip="Controls how playback scales videos that don't match this screen's resolution/aspect ratio."
          >
            <Select options={VIDEO_FIT_MODES} />
          </Form.Item>

          <Form.Item
            label="Video Canvas"
            tooltip="Optional sub-region of the screen where video is placed. Leave blank to use the full screen."
            style={{ marginBottom: 0 }}
          >
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="videoWidth" label="Width (px)">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="Full screen" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="videoHeight" label="Height (px)">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="Full screen" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="videoOffsetX" label="Offset X (px)" initialValue={0}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="videoOffsetY" label="Offset Y (px)" initialValue={0}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Screen Token & Player Config"
        open={tokenModalOpen}
        onCancel={() => {
          setTokenModalOpen(false);
          setTokenData(null);
        }}
        footer={[
          <Button key="close" onClick={() => setTokenModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={640}
        confirmLoading={tokenLoading}
      >
        {tokenData && (() => {
          const playerEnvSnippet =
            `REACT_APP_SOCKET_URL=${SOCKET_URL}\n` +
            `REACT_APP_API_URL=${SOCKET_API_URL}\n` +
            `REACT_APP_SCREEN_CODE=${tokenData.screenCode}\n` +
            `REACT_APP_SCREEN_TOKEN=${tokenData.token}`;

          const agentEnvSnippet =
            `# Screen identification & authentication\n` +
            `SCREEN_CODE=${tokenData.screenCode}\n` +
            `SCREEN_TOKEN=${tokenData.token}\n\n` +
            `# Backend connection\n` +
            `SERVER_URL=${SOCKET_URL}\n\n` +
            `# Local storage for cached playlist & downloaded videos\n` +
            `STORAGE_PATH=C:\\CloudNetPlayer\n\n` +
            `# Interval settings (in seconds)\n` +
            `HEARTBEAT_INTERVAL=60\n` +
            `SCREENSHOT_INTERVAL=300`;

          return (
          <div>
            <Paragraph>
              Use this configuration on the screen-player / cloudnet-agent install for
              <Text strong> {tokenData.screenCode}</Text>. Copy this into that machine's
              <Text code> .env</Text> file.
            </Paragraph>

            <Paragraph>
              <Text strong>Screen Code:</Text>
              <br />
              <Text code copyable>{tokenData.screenCode}</Text>
            </Paragraph>

            <Paragraph>
              <Text strong>Screen Token:</Text>
              <br />
              <Text code copyable style={{ wordBreak: 'break-all' }}>{tokenData.token}</Text>
            </Paragraph>

            <Paragraph>
              <Text strong>Screen Player (.env):</Text>
              <br />
              <Input.TextArea
                readOnly
                autoSize={{ minRows: 4, maxRows: 8 }}
                value={playerEnvSnippet}
              />
              <Button
                size="small"
                icon={<CopyOutlined />}
                style={{ marginTop: 8 }}
                onClick={() => copyToClipboard(playerEnvSnippet, 'Screen Player .env snippet')}
              >
                Copy Screen Player .env
              </Button>
            </Paragraph>

            <Paragraph>
              <Text strong>CloudNet Agent (.env):</Text>
              <br />
              <Input.TextArea
                readOnly
                autoSize={{ minRows: 6, maxRows: 10 }}
                value={agentEnvSnippet}
              />
              <Button
                size="small"
                icon={<CopyOutlined />}
                style={{ marginTop: 8 }}
                onClick={() => copyToClipboard(agentEnvSnippet, 'CloudNet Agent .env snippet')}
              >
                Copy CloudNet Agent .env
              </Button>
            </Paragraph>
          </div>
          );
        })()}
      </Modal>
    </div>
  );
}
